import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { HeartbeatPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'

export const Route = createFileRoute('/_site/for/indie-hackers')({
  component: IndieHackersPage,
  head: () => ({
    meta: [
      { title: 'Uptime Monitoring for Indie Hackers | Uptrack' },
      { name: 'description', content: 'Your side project deserves monitoring. 50 monitors on Pro for indie hackers — with 30-second checks, cron monitoring, and status pages.' },
      { property: 'og:title', content: 'Uptime Monitoring for Indie Hackers | Uptrack' },
      { property: 'og:description', content: '50 monitors on Pro for your side projects. 30s checks, cron monitoring, status pages. Simple to start.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-indie-hackers.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/indie-hackers' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For Indie Hackers', item: 'https://uptrack.app/for/indie-hackers' },
          ],
        }),
      },
    ],
  }),
})

function IndieHackersPage() {
  return (
    <FeaturePage
      section="Indie hacker monitoring"
      eyebrow="For · Indie hacker monitoring"
      title="Your side project deserves monitoring too."
      intro='You built it, shipped it, and got your first users. Now make sure it stays up. 50 monitors on Pro — enough for multiple projects. Zero cost, zero excuse.'
      featuresHeading="Ship fast, monitor reliably"
      features={[
        { title: '50 monitors on Pro', desc: 'Monitor multiple side projects with one account. Landing page, API, background jobs — cover them all.' },
        { title: '30-second checks', desc: 'Your users expect your app to be up. 30-second checks catch downtime before they tweet about it.' },
        { title: 'Cron job monitoring', desc: 'Background tasks silently fail all the time. Monitor your cron jobs, queues, and scheduled tasks.' },
        { title: 'Status page included', desc: 'Show your users you take reliability seriously. A public status page says "I care about uptime."' },
        { title: 'Simple to start', desc: 'Choose Pro, Team, or Business as your projects grow.' },
        { title: 'Setup in 60 seconds', desc: 'Enter your URL, pick alerts, done. Get back to building your product.' },
      ]}
      why={{ heading: "Don't lose users to downtime", body: "You post your project on Hacker News. Traffic spikes. Your server runs out of memory and crashes. By the time you notice, the traffic is gone and so are the signups. With Uptrack, you'd get an alert in 30 seconds — enough time to scale up and ride the wave." }}
      cta={{ heading: '30-second checks on every plan', body: 'Monitor all your side projects. Start free, or choose a plan for more.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/heartbeats">
          <HeartbeatPiece />
        </ProductFrame>
      }
    />
  )
}
